// Web implementation of @scribe/core's Uploader — slices a Blob/File into the parts the
// server's presigned multipart plan expects and PUTs each part directly to S3, then POSTs
// the accumulated ETags to the presigned complete_url. Never routes audio bytes through the
// Scribe API itself (ADR-002: content goes straight to S3 via presigned URLs).
import type { Uploader } from '@scribe/core/upload-queue';
import { getUploadBlob } from './uploadStorage';

/** Thrown on a non-2xx response from S3/the presigned endpoints. Carries `status` so
 * @scribe/core's `defaultClassifyError` (upload-queue/errors.ts) can classify it the same
 * way it classifies ScribeApiError — retryable for 429/5xx, permanent otherwise. */
class HttpStatusError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpStatusError';
    this.status = status;
  }
}

// Multipart uploads split the source blob into equal-ish chunks; the server decided
// `part_count` when issuing presigned URLs (CreateRecordingRequest.part_count), so the
// client must slice consistently with that count.
function sliceForPart(blob: Blob, partNumber: number, partCount: number): Blob {
  const partSize = Math.ceil(blob.size / partCount);
  const start = (partNumber - 1) * partSize;
  const end = Math.min(start + partSize, blob.size);
  return blob.slice(start, end);
}

function xmlEscape(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Builds the canonical S3 `CompleteMultipartUpload` XML request body. Part ETags are
 * stored quote-stripped (see `uploadPart` below); S3's documented XML shape carries the
 * ETag with its surrounding double quotes, so they are restored here. */
function buildCompleteMultipartUploadXml(parts: { partNumber: number; etag: string }[]): string {
  const partsXml = parts
    .slice()
    .sort((a, b) => a.partNumber - b.partNumber)
    .map(
      (p) =>
        `<Part><PartNumber>${p.partNumber}</PartNumber><ETag>${xmlEscape(`"${p.etag}"`)}</ETag></Part>`,
    )
    .join('');
  return `<?xml version="1.0" encoding="UTF-8"?><CompleteMultipartUpload>${partsXml}</CompleteMultipartUpload>`;
}

export const webUploader: Uploader = {
  async uploadPart({ item, partNumber, url, signal }) {
    const blob = getUploadBlob(item.id);
    if (!blob) {
      throw new Error(`No local audio data registered for upload item ${item.id}`);
    }
    const chunk = sliceForPart(blob, partNumber, item.source.partCount);
    const res = await fetch(url, {
      method: 'PUT',
      body: chunk,
      // exactOptionalPropertyTypes forbids `signal: undefined` (DOM types it as
      // AbortSignal | null, not | undefined) — only set the key when we have one.
      ...(signal ? { signal } : {}),
      headers: { 'content-type': item.source.contentType },
    });
    if (!res.ok) {
      throw new HttpStatusError(
        res.status,
        `Part ${partNumber} upload failed with status ${res.status}`,
      );
    }
    const etag = res.headers.get('etag') ?? res.headers.get('ETag');
    if (!etag) {
      throw new Error(`Part ${partNumber} response missing ETag header`);
    }
    return etag.replace(/"/g, '');
  },

  async completeUpload({ completeUrl, parts, signal }) {
    // `complete_url` is a presigned S3 `CompleteMultipartUpload` URL (see
    // backend/src/scribe/api/s3_presign.py), not an application endpoint — S3's REST API
    // requires the canonical CompleteMultipartUpload XML body (Part/PartNumber/ETag), not
    // JSON. A JSON body here would fail with a MalformedXML/SignatureDoesNotMatch response.
    // Mirrors clients/android's S3MultipartUploadClient.completeUpload.
    const xml = buildCompleteMultipartUploadXml(parts);
    const res = await fetch(completeUrl, {
      method: 'POST',
      ...(signal ? { signal } : {}),
      headers: { 'content-type': 'application/xml' },
      body: xml,
    });
    if (!res.ok) {
      throw new HttpStatusError(
        res.status,
        `Complete multipart upload failed with status ${res.status}`,
      );
    }
  },

  async abortUpload({ abortUrl }) {
    try {
      // S3's presigned `abort_multipart_upload` URL is signed for HTTP DELETE — using any
      // other method will fail signature validation.
      await fetch(abortUrl, { method: 'DELETE' });
    } catch {
      // Best-effort per the Uploader contract — the item is already terminal-failed.
    }
  },
};
